// @flow

import { useContext, useCallback } from 'react';
import * as Types from '@ta/trips.trip-types';
import Storage from '@ta/common.localstorage';
import { Visitor } from '@ta/lithium.context';
import type { TripsError } from '@ta/trips.trip-errors';
import type { SaveState } from './use-save';
import useSave from './use-save';

/**
 * Save state (see `useSave`)
 * Autosave trigger
 * Update last modified trip id
 */
export type UseAutosaveResponse = [
  SaveState,
  () => Promise<[?Types.Trip, ?TripsError]>,
  (number) => void,
];

export const MOST_RECENTLY_ADDED_TO_TRIP = 'mostRecentlyAddedToTrip';
export const MOST_RECENTLY_ADDED_TO_TRIP_TIMEOUT = 4 * 60 * 60 * 1000; // 4 hours

/**
 * Saves the passed trip id as mostRecentlyModifiedTrip value in the local storage.
 * We use the local storage because we want to share this value between components.
 * Using Redux for that is difficult as we would have to pass this value to the server
 * to be stored between page refreshes. The value will expire in 4 hours after the last save.
 * The local storage is shared between users if they re-log in the same session so we are saving
 * the current logged user id to compare on
 */
const setMostRecentlyAddedToTrip = (tripId: number, userId: ?string) => {
  if (userId) {
    Storage.set(
      MOST_RECENTLY_ADDED_TO_TRIP,
      { tripId, userId },
      MOST_RECENTLY_ADDED_TO_TRIP_TIMEOUT,
    );
  }
};

/**
 * Gets the mostRecentlyModifiedTrip value from the local storage.
 */
const getMostRecentlyAddedToTrip = (userId: ?string): ?number => {
  const storageValue = Storage.get(MOST_RECENTLY_ADDED_TO_TRIP);
  if (storageValue && storageValue.userId === userId) {
    return storageValue.tripId;
  }
  return null;
};

const DISABLED_AUTOSAVE_TYPES = new Set(['savesItem', 'bundleSave']);

/**
 * Autosave hook incapsulating business logic deciding when automatic save is applicable.
 * @param object object to save
 * @param isActive if the object is already saved in any user's trip
 * @param onExplicitSave callback for manual save
 * @returns {*[]}
 */
const useAutosave = (
  object: ?Types.TripSaveObject,
  isActive: boolean,
  onExplicitSave: () => void,
): UseAutosaveResponse => {
  const [state, save] = useSave(object);
  const { userId } = useContext(Visitor);

  const saveFlow = useCallback(() => {
    const mostRecentlyModifiedTrip = getMostRecentlyAddedToTrip(userId);
    if (!isActive && mostRecentlyModifiedTrip && !DISABLED_AUTOSAVE_TYPES.has(object?.type)) {
      return save(mostRecentlyModifiedTrip);
    }
    onExplicitSave();
    return Promise.resolve([null, null]);
  }, [isActive, object, onExplicitSave, save, userId]);

  return [state, saveFlow, tripId => setMostRecentlyAddedToTrip(tripId, userId)];
};

export default useAutosave;
